// REACT
import { createContext, useContext, useEffect, useReducer } from "react";
import type { ReactNode } from "react";

// REDUX
import { SessionRes } from "src/store/features/api/generated";
import api from "src/store/features/api";

// AMPLITUDE
import { trackEvent } from "src/Amplitude";

interface AuthContextValue extends State {
  platform: "JWT";
  login: (email: string, password: string, twoFA?: string | undefined) => Promise<void>;
  webauthnLogin: (encodedString: string) => Promise<void>;
  tokenExchange: (
    code?: string | undefined,
    email?: string | undefined,
    twoFA?: string | undefined,
  ) => Promise<void>;
  logout: () => Promise<void>;
}

interface State {
  isInitialized: boolean;
  isAuthenticated: boolean;
  // user?: User;
}

interface AuthProviderProps {
  children: ReactNode;
}

interface LoginAction {
  type: "LOGIN";
  payload: {
    // user: User | {};
  };
}

interface LogoutAction {
  type: "LOGOUT";
}

type Action = LoginAction | LogoutAction;

const initialState: State = {
  isAuthenticated: false,
  isInitialized: false,
};

const handlers: Record<string, (state: State, action?: Action) => State> = {
  LOGIN: (state: State, action: any): State => {
    return {
      ...state,
      isAuthenticated: true,
    };
  },
  LOGOUT: (state: State): State => ({
    ...state,
    isAuthenticated: false,
  }),
};

const reducer = (state: State, action: Action): State =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

const AuthContext = createContext<AuthContextValue>({
  ...initialState,
  platform: "JWT",
  login: () => Promise.resolve(),
  webauthnLogin: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  tokenExchange: () => Promise.resolve(),
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = (props: AuthProviderProps) => {
  const { children } = props;
  const [state, perform] = useReducer(reducer, initialState);
  const [backendLogin] = api.useLoginV2Mutation();
  const [webauthnAuthenticate] = api.useWebauthnTokenExchangeMutation();
  const [signOut] = api.useEndSessionMutation();

  const [tokenData] = api.useTokenExchangeMutation();

  const performLogin = () => {
    perform({ type: "LOGIN", payload: { user: {} } });
  };

  const handleLogin = (r: SessionRes) => {
    sessionStorage.setItem("access_token", r.tokens.access_token!);
    sessionStorage.setItem("refresh_token", r.tokens.refresh_token!);
    sessionStorage.setItem("id_token", r.tokens.id_token!);
    sessionStorage.setItem("permission", r.permission?.toString() || "guest");
    sessionStorage.setItem("features", r.features?.toString() || "none");
    performLogin();
  };

  const handleLoginErr = (err: Error) => {
    perform({ type: "LOGOUT" });
    throw new Error(err.message);
  };

  useEffect(() => {
    const restore = async () => {
      const refreshToken = window.sessionStorage.getItem("refresh_token");
      const accessToken = window.sessionStorage.getItem("access_token");
      const idToken = window.sessionStorage.getItem("id_token");

      if (refreshToken && accessToken && idToken) {
        performLogin();
      }
    };
    restore();
  }, []);

  const login = async (
    email: string,
    password: string,
    twoFA?: string | undefined,
  ): Promise<void> => {
    await backendLogin({
      loginDtoV2: {
        email,
        password,
        mfa: twoFA,
      },
    })
      .unwrap()
      .then((res: any) => {
        handleLogin(res);
      })
      .catch(handleLoginErr);
  };

  const webauthnLogin = async (encodedString: string): Promise<void> => {
    await webauthnAuthenticate({
      webAuthnCredentialsDto: {
        encodedString,
      },
    })
      .unwrap()
      .then((res: any) => {
        handleLogin(res);
      })
      .catch(handleLoginErr);
  };

  const tokenExchange = async (
    exchangeCode?: string | undefined,
    email?: string | undefined,
    twoFA?: string | undefined,
  ): Promise<void> => {
    try {
      if (email && twoFA) {
        await tokenData({
          tokenExchangeDto: {
            code: undefined,
            email: email,
            twoFA: twoFA,
          },
        })
          .unwrap()
          .then((resp) => {
            handleLogin(resp);
          });
      } else {
        await tokenData({
          tokenExchangeDto: {
            code: exchangeCode,
          },
        })
          .unwrap()
          .then((resp) => {
            handleLogin(resp);
          });
      }
    } catch (error) {
      const err = error as Error;
      throw err;
    }
  };

  const logout = async () => {
    window.sessionStorage.setItem("isLogout", "true");
    // Handle logout error here so the fe don't try to log the user out again if 401 is returned
    await signOut();
    window.sessionStorage.clear();
    trackEvent("User Finalised the session");
    perform({ type: "LOGOUT" });
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        platform: "JWT",
        login,
        webauthnLogin,
        logout,
        tokenExchange,
      }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
